/**
 * Core Web Vitals monitoring
 * Measures and reports key performance metrics
 * 
 * Metrics tracked:
 * - LCP (Largest Contentful Paint): Loading performance
 * - FID (First Input Delay): Interactivity
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial render
 * - TTFB (Time to First Byte): Server response time
 */

import { trackWebVitals } from './analytics';

type MetricRating = 'good' | 'needs-improvement' | 'poor';

interface Metric {
  name: string;
  value: number;
  rating: MetricRating;
  delta: number;
  id: string;
}

// Rating thresholds based on Web Vitals recommendations
const getRating = (name: string, value: number): MetricRating => {
  const thresholds: Record<string, [number, number]> = {
    LCP: [2500, 4000],      // Good: <2.5s, Poor: >4s
    FID: [100, 300],        // Good: <100ms, Poor: >300ms
    CLS: [0.1, 0.25],       // Good: <0.1, Poor: >0.25
    FCP: [1800, 3000],      // Good: <1.8s, Poor: >3s
    TTFB: [800, 1800],      // Good: <0.8s, Poor: >1.8s
    INP: [200, 500]         // Good: <200ms, Poor: >500ms
  };

  const [good, poor] = thresholds[name] || [0, 0];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
};

// Report metric to analytics
const reportMetric = (metric: Metric) => {
  console.log('[Web Vitals]', {
    name: metric.name,
    value: Math.round(metric.value),
    rating: metric.rating
  });

  trackWebVitals({
    name: metric.name,
    value: metric.value,
    rating: metric.rating
  });
};

// Initialize Web Vitals monitoring
export const initWebVitals = () => {
  // Use web-vitals library if available, otherwise use Performance Observer API
  if ('PerformanceObserver' in window) {
    // Observe LCP
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime: number; loadTime: number };
        const value = lastEntry.renderTime || lastEntry.loadTime;
        reportMetric({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
          delta: value,
          id: `lcp-${Date.now()}`
        });
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.warn('[Web Vitals] LCP observer failed:', e);
    }

    // Observe FID (deprecated, use INP instead)
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry & { processingStart: number; startTime: number }) => {
          const value = entry.processingStart - entry.startTime;
          reportMetric({
            name: 'FID',
            value,
            rating: getRating('FID', value),
            delta: value,
            id: `fid-${Date.now()}`
          });
        });
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      console.warn('[Web Vitals] FID observer failed:', e);
    }

    // Observe CLS
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as Array<PerformanceEntry & { value: number }>) {
          if (!(entry as PerformanceEntry & { hadRecentInput?: boolean }).hadRecentInput) {
            clsValue += entry.value;
          }
        }
        reportMetric({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          delta: clsValue,
          id: `cls-${Date.now()}`
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      console.warn('[Web Vitals] CLS observer failed:', e);
    }

    // Observe FCP
    try {
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries[0] as PerformanceEntry & { startTime: number };
        if (fcpEntry) {
          reportMetric({
            name: 'FCP',
            value: fcpEntry.startTime,
            rating: getRating('FCP', fcpEntry.startTime),
            delta: fcpEntry.startTime,
            id: `fcp-${Date.now()}`
          });
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (e) {
      console.warn('[Web Vitals] FCP observer failed:', e);
    }
  }

  // Report TTFB from Navigation Timing
  if ('performance' in window && 'getEntriesByType' in performance) {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const ttfb = navEntry.responseStart - navEntry.requestStart;
      reportMetric({
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        delta: ttfb,
        id: `ttfb-${Date.now()}`
      });
    }
  }
};
